import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { parseApiErrorMessage } from "../utils/apiErrorMessage";
import "../styles/athletic-outlook-modal.css";
import { apiClient } from "../utils/apiClient";

export type AthleticOutlookAthlete = {
    athleteId: number;
    firstName?: string;
    lastName?: string;
    sportName?: string;
    imageUrl?: string;
};

type LabeledOption = { value: string; label: string };

type OutlookPayload = {
    athleteId?: number;
    athleteFirstName?: string;
    athleteLastName?: string;
    athleteSportName?: string;
    athleteImageUrl?: string;
    outlook?: {
        projection?: string;
        projectionLabel?: string;
        confidence?: string;
        confidenceLabel?: string;
        notes?: string;
        submittedByName?: string;
        submittedAt?: string;
    } | null;
    canEdit?: boolean;
    projections?: LabeledOption[];
    confidences?: LabeledOption[];
};

type Props = {
    open: boolean;
    athlete: AthleticOutlookAthlete | null;
    startInEdit?: boolean;
    onClose: () => void;
    onSaved?: () => void;
};

const NOTES_MIN = 20;
const NOTES_MAX = 750;

type FieldErrors = {
    projection?: string;
    confidence?: string;
    notes?: string;
};

function validateOutlookForm(projection: string, confidence: string, notes: string): FieldErrors {
    const errors: FieldErrors = {};
    if (!projection) {
        errors.projection = "Select a current athletic projection.";
    }
    if (!confidence) {
        errors.confidence = "Select a confidence level.";
    }
    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
        errors.notes = "Enter coach notes for college counseling.";
    } else if (trimmedNotes.length < NOTES_MIN) {
        errors.notes = `Coach notes must be at least ${NOTES_MIN} characters.`;
    } else if (trimmedNotes.length > NOTES_MAX) {
        errors.notes = `Coach notes cannot exceed ${NOTES_MAX} characters.`;
    }
    return errors;
}

function initials(first?: string, last?: string): string {
    return `${first?.charAt(0) || ""}${last?.charAt(0) || ""}`.toUpperCase() || "SA";
}

function confidenceBadgeClass(value?: string): string {
    if (value === "HIGH") return "ao-badge-high";
    if (value === "MODERATE") return "ao-badge-moderate";
    if (value === "LOW_EARLY") return "ao-badge-low";
    return "";
}

function formatSubmittedAt(value?: string): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

type OutlookSelectProps = {
    id: string;
    value: string;
    options: LabeledOption[];
    placeholder: string;
    invalid?: boolean;
    describedBy?: string;
    onChange: (value: string) => void;
};

function OutlookSelect({
    id,
    value,
    options,
    placeholder,
    invalid,
    describedBy,
    onChange,
}: OutlookSelectProps) {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const selected = options.find((option) => option.value === value);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onDocClick = (event: MouseEvent) => {
            if (wrapRef.current?.contains(event.target as Node)) {
                return;
            }
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    return (
        <div className={`ao-select-wrap${invalid ? " is-invalid" : ""}`} ref={wrapRef}>
            <button
                type="button"
                id={id}
                className="ao-select-button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                onClick={() => setOpen((current) => !current)}
            >
                <span className={selected ? "ao-select-value" : "ao-select-placeholder"}>
                    {selected?.label || placeholder}
                </span>
                <span className="ao-select-caret" aria-hidden>▾</span>
            </button>
            {open && (
                <ul className="ao-select-list" role="listbox" aria-labelledby={id}>
                    {options.map((option) => {
                        const selectedOption = option.value === value;
                        return (
                            <li
                                key={option.value}
                                role="option"
                                aria-selected={selectedOption}
                                className={`ao-select-option${selectedOption ? " is-selected" : ""}`}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                            >
                                {option.label}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function AthleticOutlookModal({
    open,
    athlete,
    startInEdit = false,
    onClose,
    onSaved,
}: Props) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState<OutlookPayload | null>(null);
    const [editing, setEditing] = useState(startInEdit);
    const [projection, setProjection] = useState("");
    const [confidence, setConfidence] = useState("");
    const [notes, setNotes] = useState("");
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    useEffect(() => {
        if (!open || !athlete?.athleteId) {
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            setFieldErrors({});
            try {
                const response = await apiClient(
                    buildApiUrl(`api/athletes/${athlete.athleteId}/athletic-outlook`),
                    { headers: { Accept: "application/json" }, credentials: "include" }
                );
                if (!response.ok) {
                    setError(await parseApiErrorMessage(response, "Unable to load this assessment."));
                    return;
                }
                const json = (await response.json()) as OutlookPayload;
                if (cancelled) return;
                setData(json);
                setProjection(json.outlook?.projection ?? "");
                setConfidence(json.outlook?.confidence ?? "");
                setNotes(json.outlook?.notes ?? "");
                setEditing(startInEdit || !json.outlook);
            } catch {
                if (!cancelled) setError("Unable to load this assessment.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, athlete?.athleteId, startInEdit]);

    const firstName = data?.athleteFirstName || athlete?.firstName || "";
    const lastName = data?.athleteLastName || athlete?.lastName || "";
    const sportName = data?.athleteSportName || athlete?.sportName || "";
    const imageUrl = data?.athleteImageUrl || athlete?.imageUrl;
    const canEdit = !!data?.canEdit;
    const outlook = data?.outlook;
    const title = editing
        ? outlook
            ? "Edit Pre-Read"
            : "Add Pre-Read"
        : "Pre-Read Summary";

    const submittedLabel = useMemo(() => {
        if (!outlook?.submittedAt) return "Not yet submitted";
        const when = formatSubmittedAt(outlook.submittedAt);
        const who = outlook.submittedByName;
        if (who && when) return `Submitted by ${who} · ${when}`;
        if (who) return `Submitted by ${who}`;
        return when ? `Submitted · ${when}` : "Submitted";
    }, [outlook]);

    if (!open || !athlete) return null;

    const save = async () => {
        const nextErrors = validateOutlookForm(projection, confidence, notes);
        setFieldErrors(nextErrors);
        if (nextErrors.projection || nextErrors.confidence || nextErrors.notes) {
            return;
        }
        setSaving(true);
        setError("");
        try {
            const response = await apiClient(
                buildApiUrl(`api/athletes/${athlete.athleteId}/athletic-outlook`),
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        projection,
                        confidence,
                        notes: notes.trim(),
                    }),
                }
            );
            if (!response.ok) {
                setError(await parseApiErrorMessage(response, "Unable to save this assessment."));
                return;
            }
            onSaved?.();
            onClose();
        } catch {
            setError("Unable to save this assessment.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ao-overlay" role="presentation">
            <div
                className="ao-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="athletic-outlook-title"
            >
                <button type="button" className="ao-close" aria-label="Close" onClick={onClose}>✕</button>
                <h1 id="athletic-outlook-title" className="ao-title">{title}</h1>

                <div className="ao-athlete">
                    <div className="ao-avatar">
                        {imageUrl ? <img src={imageUrl} alt="" /> : initials(firstName, lastName)}
                    </div>
                    <div>
                        <div className="ao-athlete-name">{`${firstName} ${lastName}`.trim() || "Student-athlete"}</div>
                        <div className="ao-athlete-meta">
                            Student-Athlete{sportName ? ` · ${sportName}` : ""}
                        </div>
                    </div>
                </div>

                {error && <p className="ao-error" role="alert">{error}</p>}
                {loading && <p>Loading…</p>}

                {!loading && editing && (
                    <>
                        <div className="ao-question">
                            <label className="ao-q-label" htmlFor="ao-projection">
                                1. Current Athletic Projection <span className="ao-required">*</span>
                            </label>
                            <p className="ao-help">
                                Based on what you know today, where do you believe this student-athlete could realistically compete in college?
                            </p>
                            <OutlookSelect
                                id="ao-projection"
                                value={projection}
                                options={data?.projections ?? []}
                                placeholder="Select a level…"
                                invalid={!!fieldErrors.projection}
                                describedBy={fieldErrors.projection ? "ao-projection-error" : undefined}
                                onChange={(next) => {
                                    setProjection(next);
                                    setFieldErrors((current) => ({ ...current, projection: undefined }));
                                }}
                            />
                            {fieldErrors.projection && (
                                <p id="ao-projection-error" className="ao-field-error" role="alert">{fieldErrors.projection}</p>
                            )}
                        </div>

                        <div className="ao-question">
                            <span className="ao-q-label">
                                2. Confidence in Assessment <span className="ao-required">*</span>
                            </span>
                            <p className="ao-help">How confident are you in this projection today?</p>
                            <div
                                className={`ao-confidence${fieldErrors.confidence ? " is-invalid" : ""}`}
                                role="group"
                                aria-label="Confidence in assessment"
                                aria-invalid={!!fieldErrors.confidence}
                                aria-describedby={fieldErrors.confidence ? "ao-confidence-error" : undefined}
                            >
                                {(data?.confidences ?? []).map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`ao-chip${confidence === option.value ? " is-selected" : ""}`}
                                        onClick={() => {
                                            setConfidence(option.value);
                                            setFieldErrors((current) => ({ ...current, confidence: undefined }));
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                            {fieldErrors.confidence && (
                                <p id="ao-confidence-error" className="ao-field-error" role="alert">{fieldErrors.confidence}</p>
                            )}
                        </div>

                        <div className="ao-question">
                            <label className="ao-q-label" htmlFor="ao-notes">
                                3. Coach Notes for College Counseling <span className="ao-required">*</span>
                            </label>
                            <p className="ao-help">
                                What should the college counselor know when helping this student-athlete build their college strategy?
                                Notes must be {NOTES_MIN}–{NOTES_MAX} characters.
                            </p>
                            <textarea
                                id="ao-notes"
                                className={`ao-textarea${fieldErrors.notes ? " is-invalid" : ""}`}
                                maxLength={NOTES_MAX}
                                placeholder="Add notes for the college counselor…"
                                value={notes}
                                aria-invalid={!!fieldErrors.notes}
                                aria-describedby={fieldErrors.notes ? "ao-notes-error" : "ao-notes-count"}
                                onChange={(event) => {
                                    setNotes(event.target.value);
                                    setFieldErrors((current) => ({ ...current, notes: undefined }));
                                }}
                            />
                            {fieldErrors.notes && (
                                <p id="ao-notes-error" className="ao-field-error" role="alert">{fieldErrors.notes}</p>
                            )}
                            <span id="ao-notes-count" className={`ao-count${notes.length >= 700 ? " is-warn" : ""}`}>
                                {notes.length} / {NOTES_MAX}
                            </span>
                        </div>
                    </>
                )}

                {!loading && !editing && outlook && (
                    <>
                        <div className="ao-summary-grid">
                            <div className="ao-summary-field">
                                <span className="ao-summary-label">Current Athletic Projection</span>
                                <span className="ao-badge">{outlook.projectionLabel}</span>
                            </div>
                            <div className="ao-summary-field">
                                <span className="ao-summary-label">Confidence in Assessment</span>
                                <span className={`ao-badge ${confidenceBadgeClass(outlook.confidence)}`}>
                                    {outlook.confidenceLabel}
                                </span>
                            </div>
                        </div>
                        <div className="ao-question">
                            <span className="ao-summary-label">Coach Notes for College Counseling</span>
                            <div className="ao-quote">
                                <span className="ao-quote-mark">“</span>
                                <p>{outlook.notes}</p>
                            </div>
                        </div>
                    </>
                )}

                <div className="ao-note">
                    <span className="ao-note-icon" aria-hidden>🔒</span>
                    <div>
                        <strong>Internal school note</strong>
                        <div className="ao-note-copy">
                            This assessment is visible to authorized coaches, counselors, and administrators.
                            It is not shown directly to the student-athlete or family.
                        </div>
                    </div>
                </div>

                <div className="ao-footer">
                    <span className="ao-submitted">{submittedLabel}</span>
                    <div className="ao-actions">
                        {editing ? (
                            <>
                                <button type="button" className="ao-btn ao-btn-cancel" onClick={onClose}>Cancel</button>
                                <button
                                    type="button"
                                    className="ao-btn ao-btn-primary"
                                    onClick={save}
                                    disabled={saving || loading}
                                >
                                    {saving ? "Saving…" : "Save Pre-Read"}
                                </button>
                            </>
                        ) : (
                            <>
                                <button type="button" className="ao-btn ao-btn-cancel" onClick={onClose}>Close</button>
                                {canEdit && (
                                    <button
                                        type="button"
                                        className="ao-btn ao-btn-secondary"
                                        onClick={() => setEditing(true)}
                                    >
                                        Edit Outlook
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AthleticOutlookModal;
